import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buyersApi, type BuyerLeadStatus } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { BUYER_STATUSES, buyerLabel } from "./buyerFilters";
import { BUYER_TERMINAL_STATUSES } from "./buyerPhases";

/**
 * Move a buyer through the funnel.
 *
 * <p><b>A confirm step rather than a save-on-change dropdown</b>, unlike the lead
 * source beside it. A status change stamps a milestone server-side (L6) and those
 * stamps are forward-only — the funnel report counts them — so it is not the kind
 * of edit that should be one mis-click away. The step is also where the reason
 * goes, which a bare <c>select</c> has nowhere to put.</p>
 *
 * <p><b>Every status is offered, including the terminal three.</b> Closing a
 * buyer out as Not qualified, Nurture or Dead is an ordinary correct outcome and
 * hiding it would push specialists into leaving dead records looking live. Those
 * three REQUIRE a reason (<c>BUYER_TERMINAL_STATUSES</c>, the server's
 * <c>IsTerminal</c>), so Save stays disabled until one is typed rather than
 * sending a request the server refuses.</p>
 */
export function BuyerStatusControl({
  buyerLeadId,
  status,
  canEdit,
}: {
  buyerLeadId: string;
  status: BuyerLeadStatus;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [next, setNext] = useState<BuyerLeadStatus>(status);
  const [reason, setReason] = useState("");

  const reasonRequired = BUYER_TERMINAL_STATUSES.includes(next);
  const reasonMissing = reasonRequired && reason.trim().length === 0;

  // Rehydrate each time it opens, so a cancelled change is never re-offered as
  // if it were the record's current state.
  useEffect(() => {
    if (!open) return;
    setNext(status);
    setReason("");
  }, [open, status]);

  const save = useMutation({
    mutationFn: () =>
      buyersApi.setStatus(buyerLeadId, {
        status: next,
        reason: reason.trim().length === 0 ? null : reason.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success(`Status set to ${buyerLabel(next)}.`);
      setOpen(false);
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  if (!canEdit) return null;

  return (
    <>
      <Button
        variant="text"
        onClick={() => setOpen(true)}
        data-testid="buyer-change-status"
      >
        Change status
      </Button>

      <Modal
        open={open}
        onClose={() => (save.isPending ? undefined : setOpen(false))}
        title="Change status"
        description="Moving a buyer forward stamps the matching milestone. Milestones are kept even if the buyer later moves back."
        size="sm"
        footer={
          <div className="flex justify-end gap-space-2">
            <Button variant="text" onClick={() => setOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={save.isPending || next === status || reasonMissing}
              data-testid="buyer-status-save"
            >
              {save.isPending ? "Saving…" : "Save status"}
            </Button>
          </div>
        }
      >
        <div className="grid gap-space-3">
          <label className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">Status</span>
            <Select
              value={next}
              onChange={(e) => setNext(e.target.value as BuyerLeadStatus)}
              aria-label="Buyer status"
              data-testid="buyer-status-select"
              className="mt-space-1"
            >
              {BUYER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {buyerLabel(s)}
                </option>
              ))}
            </Select>
          </label>

          <label className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              Reason{" "}
              <span className="text-gray-mid">
                {reasonRequired ? "(required)" : "(optional)"}
              </span>
            </span>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                reasonRequired
                  ? "Why the buyer is being closed out. Recorded on the audit trail."
                  : "Why the buyer moved. Recorded on the audit trail."
              }
              aria-label="Reason for the status change"
              data-testid="buyer-status-reason"
              className="mt-space-1"
            />
          </label>

          {next === "ConvertedToBorrower" && status !== "ConvertedToBorrower" && (
            <p className="text-caption text-gray-mid m-0">
              Converting for real is the “Convert to borrower” action — it mints
              the borrower record and links the two. Setting the status by hand
              here marks the buyer converted without either.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
